import {
    ActionsLayout,
    NumberFormat,
    Page,
    PageSection,
    ResponsiveTable,
    ResponsiveTableBody,
    ResponsiveTableCell,
    ResponsiveTableFooter,
    ResponsiveTableHeaderCell,
    ResponsiveTableHeaderRow,
    ResponsiveTablePagination,
    ResponsiveTableRow,
    ResponsiveTableRowActions,
    SearchAndFiltersLayout,
    SearchInput,
    SearchSummary,
    Button,
    FlexLayout
} from '@crx-dev/hss-react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { makeRequest, useBooleanState, useSelectableItems } from '@crx-dev/hss-react-infrastructure/dist';
import { RegisterSummary } from './types/RegisterSummary';
import { PaginatedResponse } from '../../Common/types/PaginatedResponse';
import { usePaginationInUrl } from '../../../Infrastructure/pageState/usePaginationInUrl';
import { makeReturnUrl } from '../../../Infrastructure/pageState/useMakeReturnUrl';
import { downloadAndOpenFile } from '@crx-dev/hss-react-infrastructure';
import { getStoreSettingValue } from '../../Common/Helpers/SettingsHelper';
import { useStoreSettings } from '../../../Infrastructure/settings/UseStoreSettings';
import StockCheckReminderInfo from './Components/StockCheckReminderInfo';
import BulkStockCheckForm from '../Register/forms/BulkStockCheckForm';
import LocumUserRegisterForm from '../../Common/forms/LocumUserRegisterForm';
import { useUserInfo } from '../../../Infrastructure/auth/UseUserInfo';
import { logout } from '../../../Infrastructure/auth/keyCloakAuth';

function RegistersList() {
    const history = useHistory();
    const storeSettings = useStoreSettings();
    const userInfo = useUserInfo();
    const [stockCheckReminderIsEnabled, setStockCheckReminderIsEnabled] = useState(false);
    const [stockCheckReminderWeeksOverdue, setStockCheckReminderWeeksOverdue] = useState(1);
    const [isReadyToUnselectAll, setIsReadyToUnselectAll] = useState<boolean>(false);
    const [showingLocumUserRegisterForm, showLocumUserRegisterForm, hideLocumUserRegisterForm] = useBooleanState(false);

    const {
        pageState: { page, pageSize, sortField, sortDirection, search },
        actions: { setPage, setSort, setPageSize, setSearch }
    } = usePaginationInUrl({
        sortField: 'ProductName',
        sortDirection: 'asc'
    });

    const fetchStoreSettings = useCallback(() => {
        if (storeSettings.data != null) {
            setStockCheckReminderIsEnabled(
                getStoreSettingValue(storeSettings.data, 'StockCheckReminderIsEnabled') === 'True'
            );
            setStockCheckReminderWeeksOverdue(
                parseInt(getStoreSettingValue(storeSettings.data, 'StockCheckReminderWeeksOverdue'))
            );
        }
    }, [storeSettings.data]);

    const [showingBulkStockCheckForm, showBulkStockCheckForm, hideBulkStockCheckForm] = useBooleanState(false);

    const { data, isLoading, isFetching, refetch } = useQuery<PaginatedResponse<RegisterSummary[]>>(
        ['register-list', page, pageSize, sortField, sortDirection, search],
        () =>
            makeRequest({
                method: 'GET',
                path: '/api/v1/ControlDrugRegister',
                data: {
                    page,
                    pageSize,
                    sortField,
                    sortDirection,
                    search
                }
            }),
        {
            keepPreviousData: true
        }
    );

    const {
        selectableItems,
        setSelectedItem,
        selectAllItems,
        allItemsSelected,
        hasSelectedItems,
        selectedIds
    } = useSelectableItems<RegisterSummary>(data?.payload);

    const handleViewProduct = useCallback((registerId: string) => {
        let returnUrl = makeReturnUrl();
        history.push(`/registers/${registerId}?returnUrl=${returnUrl}`);
    }, []);

    const handleSearch = useCallback((value: string) => {
        setSearch(value);
    }, []);

    const handleExport = useCallback(
        () => downloadAndOpenFile('GET', `/api/v1/controldrugregister/report`, 'application/pdf'),
        []
    );

    useEffect(() => {
        fetchStoreSettings();
    }, [storeSettings]);

    useEffect(() => {
        if (!showingBulkStockCheckForm && selectableItems.length > 0 && hasSelectedItems) {
            setIsReadyToUnselectAll(!isReadyToUnselectAll);
            refetch();
        }
    }, [showingBulkStockCheckForm]);

    useEffect(() => {
        if (isReadyToUnselectAll) {
            selectAllItems(false);
        }
    }, [isReadyToUnselectAll]);

    useEffect(() => {
        if (userInfo.isLocum && userInfo.name === '') {
            showLocumUserRegisterForm();
        }
    }, [userInfo.isLocum]);

    return (
        <Page>
            <Page.Header
                title="Controlled Drug Registers"
                pageActions={
                    <Button id="record-event" onClick={() => history.push('/registers/add')}>
                        Add New Register
                    </Button>
                }
            />
            <PageSection>
                <FlexLayout justifyContent="space-between" wrap>
                    <SearchAndFiltersLayout
                        search={
                            <SearchInput
                                name="search-field"
                                onSearch={handleSearch}
                                placeholder="Search drugs"
                                defaultValue={search}
                            />
                        }
                    />
                    <ActionsLayout layout="inline">
                        <Button
                            variant="secondary"
                            id="bulkStockCheck"
                            size="small"
                            disabled={!hasSelectedItems}
                            onClick={showBulkStockCheckForm}
                        >
                            Bulk Stock Check
                        </Button>
                        <Button id="exportRegisterEntries" onClick={handleExport} variant="secondary" size="small">
                            Export
                        </Button>
                    </ActionsLayout>
                </FlexLayout>
                <SearchSummary
                    filtersConfig={[]}
                    filterValues={{}}
                    onFiltersChange={() => {}}
                    searchTerm={search}
                    onSearchChange={handleSearch}
                />
                <ResponsiveTable
                    id="RegisterSummaries"
                    collapseAt={800}
                    isLoading={isLoading}
                    isUpdating={isFetching}
                    sortParams={{ field: sortField, direction: sortDirection }}
                    hasSelectableItems
                    onSelectAll={selectAllItems}
                    allItemsSelected={allItemsSelected}
                    onSort={setSort}
                >
                    {stockCheckReminderIsEnabled && (
                        <ResponsiveTableHeaderRow>
                            <ResponsiveTableHeaderCell sortField="ProductName">Product</ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell sortField="Balance">Total Balance</ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell sortField="QuarantinedBalance">
                                Including Expired Stock
                            </ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell sortField="StockCheckedAt">
                                Stock Check
                            </ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell />
                        </ResponsiveTableHeaderRow>
                    )}
                    {!stockCheckReminderIsEnabled && (
                        <ResponsiveTableHeaderRow>
                            <ResponsiveTableHeaderCell sortField="ProductName">Product</ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell sortField="Balance">Total Balance</ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell sortField="QuarantinedBalance">
                                Including Expired Stock
                            </ResponsiveTableHeaderCell>
                            <ResponsiveTableHeaderCell />
                        </ResponsiveTableHeaderRow>
                    )}

                    <ResponsiveTableBody>
                        {data &&
                            selectableItems.map((item, i) => {
                                if (stockCheckReminderIsEnabled) {
                                    return (
                                        <ResponsiveTableRow
                                            key={i}
                                            rowId={item.id}
                                            onSelected={setSelectedItem}
                                            selected={item._selected}
                                        >
                                            <ResponsiveTableCell promoteToHeaderOnMobile>
                                                {item.productName}
                                            </ResponsiveTableCell>
                                            <ResponsiveTableCell>
                                                <NumberFormat value={item.balance} />
                                            </ResponsiveTableCell>
                                            <ResponsiveTableCell>
                                                <NumberFormat value={item.quarantineBalance} />
                                            </ResponsiveTableCell>

                                            <ResponsiveTableCell>
                                                <StockCheckReminderInfo
                                                    date={item.stockCheckedAt}
                                                    enabled={stockCheckReminderIsEnabled}
                                                    weeksOverdue={stockCheckReminderWeeksOverdue}
                                                />
                                            </ResponsiveTableCell>

                                            <ResponsiveTableRowActions>
                                                <Button
                                                    id={`view-${item.registerId}`}
                                                    size="small"
                                                    onClick={() => handleViewProduct(item.registerId)}
                                                    variant="secondary"
                                                >
                                                    View Register
                                                </Button>
                                            </ResponsiveTableRowActions>
                                        </ResponsiveTableRow>
                                    );
                                } else {
                                    return (
                                        <ResponsiveTableRow
                                            key={i}
                                            rowId={item.id}
                                            onSelected={setSelectedItem}
                                            selected={item._selected}
                                        >
                                            <ResponsiveTableCell promoteToHeaderOnMobile>
                                                {item.productName}
                                            </ResponsiveTableCell>
                                            <ResponsiveTableCell>
                                                <NumberFormat value={item.balance} />
                                            </ResponsiveTableCell>
                                            <ResponsiveTableCell>
                                                <NumberFormat value={item.quarantineBalance} />
                                            </ResponsiveTableCell>

                                            <ResponsiveTableRowActions>
                                                <Button
                                                    id={`view-${item.registerId}`}
                                                    size="small"
                                                    onClick={() => handleViewProduct(item.registerId)}
                                                    variant="secondary"
                                                >
                                                    View Register
                                                </Button>
                                            </ResponsiveTableRowActions>
                                        </ResponsiveTableRow>
                                    );
                                }
                            })}
                    </ResponsiveTableBody>
                    <ResponsiveTableFooter>
                        <ResponsiveTablePagination
                            page={page}
                            rowsPerPage={pageSize}
                            totalResults={data?.count ?? 0}
                            onPageChange={setPage}
                            onRowsPerPageChange={setPageSize}
                            showRowsPerPageSelect
                        />
                    </ResponsiveTableFooter>
                </ResponsiveTable>
            </PageSection>
            {showingBulkStockCheckForm && (
                <BulkStockCheckForm registersIds={selectedIds as string[]} onCancel={hideBulkStockCheckForm} />
            )}
            {showingLocumUserRegisterForm && (
                <LocumUserRegisterForm onSuccess={hideLocumUserRegisterForm} onCancel={logout} />
            )}
        </Page>
    );
}

export default RegistersList;
